<template>
  <div :class="bem()">
    <UpsellProgress
      v-if="upsellOptions.length && !(shopifyStore.upsoldItems.length === upsellOptions.length)"
    />
    <UpsellItems :upsells="upsellOptions" />
  </div>
</template>

<script>
export default {
  name: 'UpsellsBlock',

  props: {
    lineItems: { type: Array, default: () => [] }
  },

  async setup() {
    const shopifyStore = useShopifyStore()
    const bem = useBEM('UpsellsBlock')

    await shopifyStore.getAutomaticCodeDiscounts()

    return { bem, shopifyStore }
  },

  data() {
    return {
      customisedProducts: {},
      upsellOptions: [],
      processed: 0,
      upsellsOrdered: false,
      originalFreeShippingRemainingSpend: 0,
    }
  },

  computed: {
    skusInCart() {
      return this.lineItems.map(
        (lineItem) => lineItem.bundleSku
      )
    },
  },

  watch: {
    lineItems: {
      deep: true,
      async handler() {
        await this.constructUpsells()
      },
    },
  },

  async created() {
    // save the free shipping threshold on initial load so upsell ordering remains same when add/removing upsells & cart items
    this.originalFreeShippingRemainingSpend = this.hasFreeShipping
      ? 0
      : this.freeShippingRemainingSpend

    if(this.lineItems.length) {
      await this.constructUpsells()
    }
  },

  methods: {
    isInCart(sku) {
      return this.skusInCart.some((skuInCart) => skuInCart?.includes(sku))
    },

    async constructUpsells() {
      try {
        await this.getCustomisedProducts()
      } catch (error) {
        console.error('Error getting customised products', error)
        return
      }

      let newUpsellOptions = this.upsellOptions

      await Promise.all(
        Object.entries(this.customisedProducts).map(
          async ([lineItemId, customisedProduct]) => {
            const upsellsForProduct = await this.$upsell.getUpsellsForProduct(
              customisedProduct
            )

            upsellsForProduct.forEach((upsellForProduct) => {
              const existingUpsellIndex = this.getExistingUpsellIndex(
                newUpsellOptions,
                upsellForProduct
              )

              if (existingUpsellIndex > -1) {
                newUpsellOptions = this.existingUpsellsInCart(
                  newUpsellOptions,
                  existingUpsellIndex
                )
              } else if (upsellForProduct.product?.isInStock) {
                const isInCart = this.isInCart(upsellForProduct.product.sku)
                upsellForProduct.isInCart = isInCart
                upsellForProduct.lineItemId = lineItemId
                newUpsellOptions.push(upsellForProduct)
              }
            })

            if (customisedProduct.state.bundleParentSku) {
              return newUpsellOptions
            }

            // Handle creation and editing of polarisation upsell
            newUpsellOptions = this.polarisationUpsell(
              customisedProduct,
              newUpsellOptions,
              lineItemId
            )
          }
        )
      )

      const validUpsells = this.removeInvalidUpsells(newUpsellOptions)
      this.orderUpsells(validUpsells)
    },

    existingUpsellsInCart(newUpsellOptions, existingUpsellIndex) {
      // check if any of the available options on an upsell exist in the cart
      if (newUpsellOptions[existingUpsellIndex].availableOptions) {
        const matchedOptionInCartIndex = newUpsellOptions[
          existingUpsellIndex
        ].availableOptions.findIndex((availableOption) => {
          return this.isInCart(
            newUpsellOptions[existingUpsellIndex].baseSKU +
              '-' +
              availableOption.sku
          )
        })

        if (matchedOptionInCartIndex > -1) {
          newUpsellOptions[existingUpsellIndex].isInCart = true
          newUpsellOptions[existingUpsellIndex].availableOptions[
            matchedOptionInCartIndex
          ].isInCart = true
        }
      } else {
        // for upsells with no additional options
        newUpsellOptions[existingUpsellIndex].isInCart = this.isInCart(
          newUpsellOptions[existingUpsellIndex].product.sku
        )
      }

      return newUpsellOptions
    },

    getExistingUpsellIndex(newUpsellOptions, upsellForProduct) {
      return newUpsellOptions.findIndex((upsellOption) => {
        if (upsellOption.sku && upsellOption.key === upsellForProduct.key) {
          return upsellOption.sku === upsellForProduct.sku
        } else if (
          upsellOption.upsellSKU &&
          upsellOption.key === upsellForProduct.key
        ) {
          return upsellOption.upsellSKU === upsellForProduct.upsellSKU
        } else if (
          upsellOption.baseSKU &&
          upsellOption.key === upsellForProduct.key
        ) {
          return upsellOption.baseSKU === upsellForProduct.baseSKU
        }

        return false
      })
    },

    polarisationUpsell(customisedProduct, newUpsellOptions, lineItemId) {
      // Don't offer polarisationUpsell for prescription products
      if (customisedProduct.isPrescription)
        return newUpsellOptions

      // Don't offer polarisationUpsell for 8KO products during promo
      const globalConfigStore = useGlobalConfigStore()
      const freeGiftEnabled = globalConfigStore.isFlagEnabled('august-sale')
      const is8KO = customisedProduct.lensTech === '8ko'
      if (freeGiftEnabled && is8KO) return newUpsellOptions

      customisedProduct.lensTypeOptions.forEach((lensTypeOption) => {
        const existingUpsellIndex = newUpsellOptions.findIndex(
          (upsellOption) => {
            if (upsellOption.mode !== 'lens_type') return false

            return upsellOption.polarisationUpsell.sku === lensTypeOption.sku
          }
        )

        if (
          existingUpsellIndex === -1 &&
          lensTypeOption.amount_float >
            customisedProduct.lensChoice.amountFloat &&
          lensTypeOption.inStock
        ) {
          // If the polarisation upsell doesn't already exist then push into upsell array
          newUpsellOptions.push({
            mode: 'lens_type',
            lineItemId,
            product: customisedProduct,
            polarisationUpsell: lensTypeOption,
            title: 'Polarised Upgrade',
            sku: customisedProduct.sku,
            isInCart: false,
          })
        } else if (existingUpsellIndex > -1) {
          // If upsell already exists check if it has been added to the basket and mark in cart if so
          newUpsellOptions[existingUpsellIndex] = {
            mode: 'lens_type',
            lineItemId,
            product: customisedProduct,
            polarisationUpsell: lensTypeOption,
            title: 'Polarised Upgrade',
            sku: customisedProduct.sku,
            isInCart:
              this.checkPolarisationUpsellIsInCart(newUpsellOptions[existingUpsellIndex])
          }
        }
      })

      return newUpsellOptions
    },

    checkPolarisationUpsellIsInCart(upsellOption) {
      return this.lineItems.findIndex((lineItem) => {
        return (
          lineItem.bundleSku.includes(upsellOption.polarisationUpsell.sku) &&
          lineItem.bundleOriginalCost ===
            upsellOption.polarisationUpsell
                .price *
                lineItem.bundleQuantity
        )
      }) !== -1
    },

    removeInvalidUpsells(newUpsellOptions) {
      return newUpsellOptions.filter((newUpsellOption) => {
        return (
          Object.entries(this.customisedProducts).findIndex(
            ([lineItemId, customisedProduct]) => {
              // if we cant find a matching line item in the cart for the upsell then remove the upsell
              if (newUpsellOption.mode === 'lens_type') {
                // for lens type use sku without lens tech so that lens kits can remain when upgraded
                return (
                  newUpsellOption.product.skuWithoutLensTech ===
                  customisedProduct.skuWithoutLensTech
                )
              }

              return newUpsellOption.lineItemId === lineItemId
            }
          ) !== -1
        )
      })
    },

    orderUpsells(newUpsellOptions) {
      this.upsellOptions = newUpsellOptions.sort((a, b) => a.order - b.order)
    },

    async getCustomisedProducts() {
      this.customisedProducts = Object.fromEntries(
        await Promise.all(
          this.lineItems.map(async (lineItem) => {
            return [
              lineItem.bundleBaseSku,
              await this.$product.getCustomisedProduct(
                lineItem.bundleBaseSku,
                {
                  sku: lineItem.bundleSku,
                  bundleParentSku: lineItem.bundleParentSku
                }
              ),
            ]
          })
        )
      )
    },
  },
}
</script>
